import { useMemo } from "react";
import { stratify, treemap, HierarchyNode } from "d3";
import { TradeFlow, TradeDirection, TradeDirectionSelectorString } from "@atlas-int/data/types";
import { computeMonetaryValueByTradeFlow, UIView } from "@atlas-int/data";
import { TreemapRootResult, DataTransformationResult } from "../types";
import { tilingMethod } from "../constants";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";

interface BuildHierarchyParams {
  data: EnrichedTradeDataItem[];
  width: number;
  height: number;
  view: UIView;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  tradeDirectionSelector?: TradeDirectionSelectorString;
}

const buildHierarchy = ({
  data,
  width,
  height,
  view,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector,
}: BuildHierarchyParams): HierarchyNode<EnrichedTradeDataItem> | undefined => {
  if (!data || data.length === 0) return undefined;

  let root: HierarchyNode<EnrichedTradeDataItem>;
  try {
    const s = stratify<EnrichedTradeDataItem>();
    if (view === UIView.Products) {
      root = s
        .id((d) => d.productId)
        .parentId((d) => d.topLevelParent)(data);
    } else if (view === UIView.Markets) {
      root = s
        .id((d) => d.id as string)
        .parentId((d) => d.topLevelParent as string)(data);
    } else {
      return undefined;
    }
  } catch {
    return undefined;
  }

  root
    .sum((d: EnrichedTradeDataItem) =>
      computeMonetaryValueByTradeFlow({
        datum: d,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
      }),
    )
    .sort(
      (
        a: HierarchyNode<EnrichedTradeDataItem>,
        b: HierarchyNode<EnrichedTradeDataItem>,
      ) => {
        if (
          view === UIView.Products &&
          (a.data.groupedProduct || b.data.groupedProduct)
        ) {
          return 1;
        }
        return a.value !== undefined && b.value !== undefined
          ? b.value - a.value
          : 0;
      },
    );

  treemap().size([width, height]).tile(tilingMethod)(root);
  return root;
};

interface UseTreemapHierarchyProps {
  dataTransformationResult: DataTransformationResult | null;
  chartWidth: number;
  chartHeight: number;
  view: UIView;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  tradeDirectionSelector?: TradeDirectionSelectorString;
}

export const useTreemapHierarchy = ({
  dataTransformationResult,
  chartWidth,
  chartHeight,
  view,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector,
}: UseTreemapHierarchyProps): TreemapRootResult => {
  return useMemo((): TreemapRootResult => {
    const noDataResult = (
      partial?: Partial<TreemapRootResult>,
    ): TreemapRootResult => ({
      root: undefined,
      servicesRoot: undefined,
      nonServicesRoot: undefined,
      totalSum: undefined,
      locationHasServices: undefined,
      servicesProportion: undefined,
      nonServicesProportion: undefined,
      noDataToDisplay: true,
      findInVizOptions: undefined,
      products: undefined,
      ...partial,
    });

    if (!dataTransformationResult) {
      return noDataResult();
    }

    const {
      dataWithRoot,
      servicesDataWithRoot,
      nonServicesDataWithRoot,
      totalSum,
      locationHasServices,
      servicesProportion,
      nonServicesProportion,
      products,
    } = dataTransformationResult;

    if (!dataWithRoot) {
      return noDataResult({
        totalSum,
        locationHasServices,
        servicesProportion,
        nonServicesProportion,
        products: products as EnrichedTradeDataItem[] | undefined,
      });
    }

    const hierarchyParams = {
      view,
      tradeFlow,
      tradeDirection,
      tradeDirectionSelector,
    };

    const root = buildHierarchy({
      data: dataWithRoot as EnrichedTradeDataItem[],
      width: chartWidth,
      height: chartHeight,
      ...hierarchyParams,
    });

    const servicesRoot =
      locationHasServices && servicesDataWithRoot && servicesDataWithRoot.length > 0
        ? buildHierarchy({
            data: servicesDataWithRoot as EnrichedTradeDataItem[],
            width: chartWidth * (servicesProportion || 0),
            height: chartHeight,
            ...hierarchyParams,
          })
        : undefined;

    const nonServicesRoot =
      locationHasServices && nonServicesDataWithRoot && nonServicesDataWithRoot.length > 0
        ? buildHierarchy({
            data: nonServicesDataWithRoot as EnrichedTradeDataItem[],
            width: chartWidth * (nonServicesProportion || 1),
            height: chartHeight,
            ...hierarchyParams,
          })
        : undefined;

    return {
      root,
      servicesRoot,
      nonServicesRoot,
      totalSum,
      locationHasServices,
      servicesProportion,
      nonServicesProportion,
      noDataToDisplay: !root ? true : undefined,
      findInVizOptions: dataWithRoot,
      products: products as EnrichedTradeDataItem[] | undefined,
    };
  }, [
    dataTransformationResult,
    chartWidth,
    chartHeight,
    view,
    tradeFlow,
    tradeDirection,
    tradeDirectionSelector,
  ]);
};
